import { HubToHubComponent } from "@/app/infrastructures/dependencies/modules/HubToHubComponent";
import { 
  Action,
  Module,
  Mutation,
  VuexModule,
  getModule
} from "vuex-module-decorators";
import store from "@/store";
import { 
  GenerateInHubScanRequest,
  AddProsesManifest
} from "@/data/payload/api/HubToHubApiRequest";
import {
  HubManifestTemporaryItem,
  InHubScanFailed,
  InHubScanGenerate,
  InHubScanGenerateList,
  InHubScanTemporaryListRequest,
  HubInOutManifestRequest,
  HubInOutManifestEntities,
  ResponsePayload,
  HubInOutManifestExcelRequest,
  HubInOutManifestExcel,
  HubManifestTemporaryList
} from "@/domain/entities/HubToHub";
import { MainAppController } from "./MainAppController";
import { HubToHubPresenter } from "@/app/ui/presenters/HubToHubPresenter";
import { storeDatatoLocalStorage } from "@/app/infrastructures/misc/LocalStorage";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import { 
  FAILED_ERROR,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { container } from "tsyringe";
import { Pagination } from "@/domain/entities/Pagination";
import { AccountController } from "./AccountController";

HubToHubComponent.init();
@Module({
  namespaced: true,
  dynamic: true,
  store,
  name: "hub-to-hub",
})
class HubToHubStore extends VuexModule {
  public InHubScanGenerateData: InHubScanGenerate[] = [];
  public isLoading = false;
  public scanFailedData: InHubScanFailed[] = [];
  public InHubScanTemporaryList: HubManifestTemporaryItem[] = [];
  public inHubScanPagination: Pagination = new Pagination(1, 20, 0);
  public isLoadingTemporaryList = false;
  public isLoadingOutHubList = false;
  public isLoadingInHubList = false;
  public OutHubDataManifestList = new HubInOutManifestEntities(new Pagination(1, 20), []);
  public InHubDataManifestList = new HubInOutManifestEntities(new Pagination(1, 20), []);
  public startDate: any = "";
  public endDate: any = "";
  public search = "";
  public inHubStartDate: any = "";
  public inHubEndDate: any = "";
  public inHubSearch = "";

  @Action
  public generateInHubScan(
    payload: GenerateInHubScanRequest
  ): Promise<InHubScanGenerateList> {
    this.setInHubScanGenerateData(new InHubScanGenerateList());
    MainAppController.showLoading();
    this.setIsLoading(true);
    const presenter = container.resolve(HubToHubPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .generateInHubScan(payload)
        .then((res) => {
          this.setInHubScanGenerateData(res);
          resolve(res);
        })
        .catch((err) => {
          const message = err.response.data.message;

          if (typeof message !== "string") {
            let scanFailedData = this.scanFailedData;
            let errorMessage = err.response.data.message.id;

            if (AccountController.accountData.countryCode !== "id") {
              errorMessage = err.response.data.message.en;
            }

            if (
              this.scanFailedData.find(
                (data) => data.sttOrBag === payload.scanNo
              )
            ) {
              scanFailedData = scanFailedData.map((data) => {
                if (data.sttOrBag === payload.scanNo) {
                  return {
                    ...data,
                    errorMessage
                  };
                }

                return data;
              });
            } else {
              scanFailedData.unshift({
                sttOrBag: payload.scanNo,
                errorMessage
              });
            }

            this.setScanFailedData(scanFailedData);
            if (payload.status === "OUT-HUB") {
              storeDatatoLocalStorage("out-hub-scan-failed", scanFailedData);
            } else {
              storeDatatoLocalStorage("in-hub-scan-failed", scanFailedData);
            }
          }

          reject(typeof message === "string" ? err.response : err);
        })
        .finally(() => {
          MainAppController.closeLoading();
          this.setIsLoading(false);
        });
    });
  }

  @Action
  public getListHubManifestTemporary(
    params: InHubScanTemporaryListRequest
  ): Promise<HubManifestTemporaryList> {
    this.setIsLoadingTemporaryList(true);
    const presenter = container.resolve(HubToHubPresenter);

    return new Promise((resolve, reject) => {
      presenter
        .getListHubManifestTemporary(params)
        .then(res => {
          this.setInHubScanTemporaryData(res.data);
          this.setInHubScanPagination(res.pagination);
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;
          MainAppController.showErrorMessage(
            typeof message === "string"
              ? new ErrorMessageEntities({
                  message: message,
                  title: "Server Error",
                  type: FAILED_ERROR
                })
              : parsingErrorResponse(err, "Error")
          );
          reject(err);
        })
        .finally(() => {
          this.setIsLoadingTemporaryList(false);
        });
    });
  }

  
  @Action
  async getHubManifestDetail(id: number) {
    const presenter = container.resolve(HubToHubPresenter);
    return presenter.getHubManifestDetail(id);
  }

  @Action
  public getHubOutManifestList(
    params: HubInOutManifestRequest
  ): Promise<HubInOutManifestEntities> {
    const presenter = container.resolve(HubToHubPresenter);
    this.setIsLoadingOutHubList(true);

    return new Promise((resolve, reject) => {
      presenter
        .getHubOutManifestList(params)
        .then((res: HubInOutManifestEntities) => {
          this.setOutHubManifestData(res);
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;
          const code = err.response.data.code;

          if (code !== 400) {
            MainAppController.showErrorMessage(
              typeof message === "string"
                ? new ErrorMessageEntities({
                    message: message,
                    title: "Server Error",
                    type: FAILED_ERROR
                  })
                : parsingErrorResponse(err, "Error")
            );
          }
          reject(err);
        })
        .finally(() => {
          this.setIsLoadingOutHubList(false);
        });
    });
  }
  
  @Action
  public getHubInManifestList(
    params: HubInOutManifestRequest
  ): Promise<HubInOutManifestEntities> {
    const presenter = container.resolve(HubToHubPresenter);
    this.setIsLoadingInHubList(true);

    return new Promise((resolve, reject) => {
      presenter
        .getHubInManifestList(params)
        .then((res: HubInOutManifestEntities) => {
          this.setInHubManifestData(res);
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;
          const code = err.response.data.code;
          if (code !== 400) {
            MainAppController.showErrorMessage(
              typeof message === "string"
                ? new ErrorMessageEntities({
                    message: message,
                    title: "Server Error",
                    type: FAILED_ERROR
                  })
                : parsingErrorResponse(err, "Error")
            );
          }
          reject(err);
        })
        .finally(() => {
          this.setIsLoadingInHubList(false);
        });
    });
  }

  @Mutation
  public setInHubScanGenerateData(data: InHubScanGenerateList): void {
    this.InHubScanGenerateData = data.data;
  }

  @Mutation
  public setIsLoading(value: boolean) {
    this.isLoading = value;
  }

  @Mutation
  public setScanFailedData(data: InHubScanFailed[]): void {
    this.scanFailedData = data;
  }

  @Mutation
  public setInHubScanTemporaryData(data: HubManifestTemporaryItem[]): void {
    this.InHubScanTemporaryList = data;
  }

  @Mutation
  public setInHubScanPagination(data: Pagination): void {
    this.inHubScanPagination = data;
  }
  
  @Mutation
  public setOutHubManifestData(data: HubInOutManifestEntities): void {
    this.OutHubDataManifestList = data;
  }
  
  @Mutation
  public setInHubManifestData(data: HubInOutManifestEntities): void {
    this.InHubDataManifestList = data;
  }

  @Mutation
  public setIsLoadingTemporaryList(bool: boolean): void {
    this.isLoadingTemporaryList = bool;
  }
  @Action
  public async processHubManifest(params: AddProsesManifest) {
    const presenter = container.resolve(HubToHubPresenter);
    return presenter
      .processHubManifest(params)
      .then((res: ResponsePayload) => res)
      .finally(() => {
      MainAppController.closeLoading();
    });
  }
  
  @Mutation
  public setIsLoadingOutHubList(bool: boolean): void {
    this.isLoadingOutHubList = bool;
  }
  
  @Mutation
  public setIsLoadingInHubList(bool: boolean): void {
    this.isLoadingInHubList = bool;
  }

  @Mutation
  public setStartDate(val: any) {
    this.startDate = val;
  }

  @Mutation
  public setEndDate(val: any) {
    this.endDate = val;
  }

  @Mutation
  public setFirstPage() {
    this.OutHubDataManifestList.pagination.page = 1;
  }

  @Mutation
  public setSearch(val: string) {
    this.search = val;
  }

  @Mutation
  public setInHubStartDate(val: any) {
    this.inHubStartDate = val;
  }

  @Mutation
  public setInHubEndDate(val: any) {
    this.inHubEndDate = val;
  }

  @Mutation
  public setInHubFirstPage() {
    this.InHubDataManifestList.pagination.page = 1;
  }

  @Mutation
  public setInHubSearch(val: string) {
    this.inHubSearch = val;
  }

   @Action
    async getHubManifestExcel(params: HubInOutManifestExcelRequest): Promise<HubInOutManifestExcel> {
      MainAppController.closeErrorMessage();
      MainAppController.showLoading();
      const presenter = container.resolve(HubToHubPresenter);
      return new Promise((resolve, reject) => {
      presenter
        .getHubManifestExcel(params)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          const message = err.response.data.message;
          MainAppController.showErrorMessage(
            typeof message === "string"
              ? new ErrorMessageEntities({
                  message: message,
                  title: "Server Error",
                  type: FAILED_ERROR
                })
              : parsingErrorResponse(err, "Error")
          );
          reject(err);
        })
        .finally(() => {
          MainAppController.closeLoading();
        });
    });
    }
}

export const HubToHubController = getModule(HubToHubStore);
